import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BusinessIdea } from '../services/businessIdeaTypes';
import { getSavedBusinessIdeas, deleteBusinessIdea } from '../services/savedIdeasService';
import { toast } from 'react-hot-toast';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from '../components/ui/Card';
import Button from '../components/ui/Button';
import { SkeletonCard } from '../components/ui/LoadingSkeleton';
import { EmptyBusinessIdeas } from '../components/ui/EmptyState';

const SavedIdeas: React.FC = () => {
  const [savedIdeas, setSavedIdeas] = useState<BusinessIdea[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchIdeas = async () => {
      try {
        setIsLoading(true);
        const ideas = await getSavedBusinessIdeas();
        // Map Supabase field names to our BusinessIdea interface field names
        const mappedIdeas = ideas.map(idea => ({
          ...idea,
          id: idea.id,
          title: idea.title,
          description: idea.description || '',
          potentialMarket: idea.targetMarket || '',
          industry: idea.industry || 'Unknown',
          createdAt: idea.createdAt
        }));
        setSavedIdeas(mappedIdeas);
      } catch (error) {
        console.error('Error fetching saved ideas:', error);
        toast.error('Failed to load saved ideas');
      } finally {
        setIsLoading(false);
      }
    };

    fetchIdeas();
  }, []);

  useEffect(() => {
    const handler = () => {
      void (async () => {
        try {
          const ideas = await getSavedBusinessIdeas();
          const mappedIdeas = ideas.map(idea => ({
            ...idea,
            id: idea.id,
            title: idea.title,
            description: idea.description || '',
            potentialMarket: idea.targetMarket || '',
            industry: idea.industry || 'Unknown',
            createdAt: idea.createdAt
          }));
          setSavedIdeas(mappedIdeas);
        } catch {
        }
      })();
    };

    window.addEventListener('bp-data-changed', handler);
    return () => window.removeEventListener('bp-data-changed', handler);
  }, []);

  const handleGeneratePlan = (idea: BusinessIdea) => {
    navigate('/business-plan/new', { state: { selectedIdea: idea } });
  };

  const handleDelete = async (ideaId: string) => {
    if (window.confirm('Are you sure you want to delete this idea?')) {
      try {
        await deleteBusinessIdea(ideaId);
        setSavedIdeas(savedIdeas.filter(idea => idea.id !== ideaId));
        toast.success('Idea deleted successfully');
        try {
          window.dispatchEvent(new Event('bp-data-changed'));
        } catch {
        }
      } catch (error) {
        console.error('Error deleting idea:', error);
        toast.error('Failed to delete idea');
      }
    }
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-8">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Saved Business Ideas</h1>
          <p className="text-gray-600 mt-2">Your collection of business ideas ready to turn into plans</p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={() => navigate('/idea-generator')}
          icon="✨"
        >
          Generate New Idea
        </Button>
      </div>

      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {Array.from({ length: 4 }).map((_, i) => (
            <SkeletonCard key={i} />
          ))}
        </div>
      ) : savedIdeas.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {savedIdeas.map((idea) => (
            <Card key={idea.id} variant="elevated" hoverable>
              <CardHeader>
                <CardTitle>{idea.title}</CardTitle>
                <CardDescription className="mt-2">{idea.description}</CardDescription>
              </CardHeader>
              
              <CardContent>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-700">Market:</span>
                    <p className="text-gray-600 mt-1">{idea.potentialMarket}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-700">Investment:</span>
                    <p className="text-gray-600 mt-1">${idea.estimatedStartupCost?.totalCost?.toLocaleString() || 0}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-700">Industry:</span>
                    <p className="text-gray-600 mt-1">{idea.industry || 'Unknown'}</p>
                  </div>
                  <div className="bg-gray-50 p-2 rounded">
                    <span className="font-semibold text-gray-700">Created:</span>
                    <p className="text-gray-600 mt-1">{idea.createdAt ? new Date(idea.createdAt).toLocaleDateString() : 'N/A'}</p>
                  </div>
                </div>
              </CardContent>
              
              <CardFooter>
                <Button
                  variant="success"
                  size="md"
                  onClick={() => handleGeneratePlan(idea)}
                  icon="📝"
                  className="flex-1"
                >
                  Generate Plan
                </Button>
                <Button
                  variant="danger"
                  size="md"
                  onClick={() => handleDelete(idea.id || '')}
                  icon="🗑️"
                  className="flex-1"
                >
                  Delete
                </Button>
              </CardFooter>
            </Card>
          ))}
        </div>
      ) : (
        <EmptyBusinessIdeas onGenerate={() => navigate('/idea-generator')} />
      )}
    </div>
  );
};

export default SavedIdeas;
